import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const contactJsonLd = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: "Contact DH Cleaning Services",
  description: "Get in touch with DH Cleaning Services Melbourne for a free cleaning quote.",
  mainEntity: {
    "@type": "LocalBusiness",
    name: "DH Cleaning Services",
    telephone: "+61400000000",
    email: "info@dhcleaning.com.au",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Melbourne",
      addressRegion: "VIC",
      addressCountry: "AU",
    },
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "07:00",
      closes: "18:00",
    },
  },
};

const contactSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100),
  email: z.string().trim().email("Invalid email address").max(255),
  phone: z.string().trim().min(1, "Phone number is required").max(20),
  message: z.string().trim().min(1, "Message is required").max(2000),
});

type ContactFormValues = z.infer<typeof contactSchema>;

const contactInfo = [
  { icon: Phone, label: "Phone", value: "0400 000 000", href: "tel:+61400000000" },
  { icon: Mail, label: "Email", value: "info@dhcleaning.com.au", href: "mailto:info@dhcleaning.com.au" },
  { icon: MapPin, label: "Service Area", value: "Melbourne, VIC & surrounding suburbs" },
  { icon: Clock, label: "Business Hours", value: "Mon–Sat: 7AM – 6PM" },
];

const Contact = () => {
  const { toast } = useToast();
  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: { name: "", email: "", phone: "", message: "" },
  });

  const onSubmit = (data: ContactFormValues) => {
    toast({ title: "Message sent!", description: "We'll get back to you as soon as possible." });
    form.reset();
  };

  return (
    <Layout>
      <SEO
        title="Contact Us — Get a Free Cleaning Quote"
        description="Contact DH Cleaning Services Melbourne. Call 0400 000 000 or send us a message for a free cleaning quote. Mon–Sat 7AM–6PM."
        canonical="/contact"
        keywords="contact cleaning service Melbourne, cleaning quote Melbourne, DH Cleaning phone number"
        jsonLd={contactJsonLd}
      />
      <section className="bg-gradient-to-br from-primary/10 via-background to-accent/5 py-16 lg:py-20">
        <div className="container text-center">
          <motion.div {...fadeUp}>
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-foreground mb-4">Contact Us</h1>
            <p className="text-lg text-muted-foreground max-w-lg mx-auto">
              Get in touch for a free quote or to book your clean today.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Form */}
            <motion.div {...fadeUp} className="lg:col-span-3">
              <Card>
                <CardContent className="p-6 lg:p-8">
                  <h2 className="font-display text-2xl font-bold text-foreground mb-6">Send Us a Message</h2>
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <FormField control={form.control} name="name" render={({ field }) => (
                          <FormItem>
                            <FormLabel>Full Name</FormLabel>
                            <FormControl><Input placeholder="John Smith" {...field} /></FormControl>
                            <FormMessage />
                          </FormItem>
                        )} />
                        <FormField control={form.control} name="email" render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl><Input placeholder="john@example.com" {...field} /></FormControl>
                            <FormMessage />
                          </FormItem>
                        )} />
                      </div>
                      <FormField control={form.control} name="phone" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone Number</FormLabel>
                          <FormControl><Input placeholder="0400 000 000" {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                      <FormField control={form.control} name="message" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Message</FormLabel>
                          <FormControl><Textarea rows={5} placeholder="Tell us about your cleaning needs…" {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                      <Button type="submit" size="lg" className="w-full sm:w-auto px-8">Send Message</Button>
                    </form>
                  </Form>
                </CardContent>
              </Card>
            </motion.div>

            {/* Contact Info */}
            <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.2 }} className="lg:col-span-2 space-y-6">
              <h2 className="font-display text-2xl font-bold text-foreground mb-2">Get in Touch</h2>
              <address className="space-y-5 not-italic">
                {contactInfo.map((c) => (
                  <div key={c.label} className="flex items-start gap-4">
                    <div className="h-11 w-11 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <c.icon className="h-5 w-5 text-primary" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{c.label}</p>
                      {c.href ? (
                        <a href={c.href} className="text-sm text-muted-foreground hover:text-primary transition-colors">{c.value}</a>
                      ) : (
                        <p className="text-sm text-muted-foreground">{c.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </address>

              {/* Map */}
              <div className="mt-8">
                <h3 className="font-display font-semibold text-foreground mb-3">Service Area</h3>
                <div className="rounded-xl overflow-hidden border border-border">
                  <iframe
                    title="DH Cleaning Service Area — Melbourne, VIC"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d403370.00971577845!2d144.49266785!3d-37.97115225!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6ad646b5d2ba4df7%3A0x4045675218cce7e0!2sMelbourne%20VIC!5e0!3m2!1sen!2sau!4v1700000000000!5m2!1sen!2sau"
                    width="100%"
                    height="250"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
